import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { Toaster, toast } from "sonner";

// Always use the proxied API path and let Nginx forward `/api` to the backend.
const API = '/api';
axios.defaults.headers.common['Accept'] = 'application/json';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("amt_token"));
  const [userType, setUserType] = useState(localStorage.getItem("amt_user_type"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token && userType) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, userType]);

  const fetchUser = async () => {
    try {
      const endpoint = userType === "admin" ? "/amt778797/me" : "/auth/me";
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = null) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        totp_code: totpCode
      });
      const { access_token, agent } = response.data;
      localStorage.setItem("amt_token", access_token);
      localStorage.setItem("amt_user_type", "agent");
      setToken(access_token);
      setUserType("agent");
      setUser(agent);
      return agent;
    } catch (err) {
      // Normalize axios error so callers can inspect response.data reliably
      const payload = err?.response?.data || null;
      const status = err?.response?.status || err?.status || null;
      const statusText = err?.response?.statusText || null;
      const normalized = new Error("Login failed");
      normalized.response = { data: payload, status, statusText };
      throw normalized;
    }
  };

  const adminLogin = async (email, password) => {
    const response = await axios.post(`${API}/amt778797/login`, { email, password });
    const { access_token, admin } = response.data;
    localStorage.setItem("amt_token", access_token);
    localStorage.setItem("amt_user_type", "admin");
    setToken(access_token);
    setUserType("admin");
    setUser(admin);
    return admin;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, agent } = response.data;
    localStorage.setItem("amt_token", access_token);
    localStorage.setItem("amt_user_type", "agent");
    setToken(access_token);
    setUserType("agent");
    setUser(agent);
    return agent;
  };

  const logout = () => {
    localStorage.removeItem("amt_token");
    localStorage.removeItem("amt_user_type");
    setToken(null);
    setUserType(null);
    setUser(null);
  };

  const updateUser = (data) => {
    setUser(prev => ({ ...prev, ...data }));
  };

  return (
    <AuthContext.Provider value={{ user, token, userType, loading, login, adminLogin, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Helper
const api = {
  get: async (url, token) => {
    const response = await axios.get(`${API}${url}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  post: async (url, data, token) => {
    const response = await axios.post(`${API}${url}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  put: async (url, data, token) => {
    const response = await axios.put(`${API}${url}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  delete: async (url, token) => {
    const response = await axios.delete(`${API}${url}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Protected Route
const ProtectedRoute = ({ children, requiredType = "agent" }) => {
  const { user, userType, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={requiredType === "admin" ? "/amt778797/login" : "/login"} state={{ from: location }} replace />;
  }

  if (userType !== requiredType) {
    return <Navigate to={userType === "admin" ? "/amt778797/dashboard" : "/dashboard"} replace />;
  }

  return children;
};

// ============== COMPONENTS ==============

// Agent Navbar
const AgentNavbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-emerald-100 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent cursor-pointer" onClick={() => navigate("/dashboard")} data-testid="logo">
              AMT Partner
            </span>
          </div>
          
          <div className="hidden md:flex items-center space-x-2">
            <NavLink to="/dashboard" label="Dashboard" />
            <NavLink to="/referrals" label="Referrals" />
            <NavLink to="/commissions" label="Commissions" />
            <NavLink to="/wallet" label="Wallet" />
            <NavLink to="/links" label="My Links" />
            <NavLink to="/recommendations" label="AI Insights" />
            <NavLink to="/profile" label="Profile" />
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <span className="text-gray-600 text-sm">{user?.name}</span>
            <button
              onClick={logout}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition"
              data-testid="logout-btn"
            >
              Logout
            </button>
          </div>

          <div className="md:hidden">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-700 p-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-emerald-100">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <MobileNavLink to="/dashboard" label="Dashboard" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/referrals" label="Referrals" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/commissions" label="Commissions" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/wallet" label="Wallet" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/links" label="My Links" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/recommendations" label="AI Insights" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/profile" label="Profile" onClick={() => setMobileMenuOpen(false)} />
            <button onClick={logout} className="w-full text-left px-3 py-2 text-red-500 hover:bg-red-50 rounded-md">
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Admin Navbar
const AdminNavbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-amber-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent cursor-pointer" onClick={() => navigate("/amt778797/dashboard")}>
              AMT Partner
            </span>
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded">ADMIN</span>
          </div>
          
          <div className="hidden md:flex items-center space-x-2">
            <AdminNavLink to="/amt778797/dashboard" label="Dashboard" />
            <AdminNavLink to="/amt778797/applications" label="Applications" />
            <AdminNavLink to="/amt778797/agents" label="Partners" />
            <AdminNavLink to="/amt778797/users" label="Users" />
            <AdminNavLink to="/amt778797/products" label="Products" />
            <AdminNavLink to="/amt778797/referrals" label="Referrals" />
            <AdminNavLink to="/amt778797/api-keys" label="API Keys" />
            <AdminNavLink to="/amt778797/webhook-logs" label="Logs" />
            <AdminNavLink to="/amt778797/payouts" label="Payouts" />
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-amber-700 text-sm font-medium">{user?.name}</span>
            <button
              onClick={logout}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const NavLink = ({ to, label }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <button
      onClick={() => navigate(to)}
      className={`px-3 py-2 text-sm font-medium rounded-lg transition ${
        isActive 
          ? "bg-emerald-500 text-white" 
          : "text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
      }`}
      data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
    >
      {label}
    </button>
  );
};

const AdminNavLink = ({ to, label }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <button
      onClick={() => navigate(to)}
      className={`px-3 py-2 text-sm font-medium rounded-lg transition ${
        isActive 
          ? "bg-amber-500 text-white" 
          : "text-gray-600 hover:bg-amber-50 hover:text-amber-700"
      }`}
    >
      {label}
    </button>
  );
};

const MobileNavLink = ({ to, label, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <button
      onClick={() => { navigate(to); onClick(); }}
      className={`w-full text-left px-3 py-2 rounded-md ${
        isActive ? "bg-emerald-500 text-white" : "text-gray-600 hover:bg-emerald-50"
      }`}
    >
      {label}
    </button>
  );
};

// Stat Card
const StatCard = ({ title, value, subtitle, icon, color = "emerald" }) => {
  const colors = {
    emerald: "from-emerald-500 to-emerald-600",
    green: "from-green-500 to-green-600",
    yellow: "from-yellow-500 to-yellow-600",
    gold: "from-amber-500 to-amber-600",
    blue: "from-blue-500 to-blue-600",
    orange: "from-orange-500 to-orange-600"
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className={`text-3xl font-bold mt-1 bg-gradient-to-r ${colors[color]} bg-clip-text text-transparent`}>
            {value}
          </p>
          {subtitle && <p className="text-gray-400 text-sm mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${colors[color]} flex items-center justify-center shadow-lg`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// ============== AGENT PAGES ==============

// ============== LANDING PAGE ==============
const LandingPage = () => {
  const navigate = useNavigate();
  const logoUrl = "https://customer-assets.emergentagent.com/job_amt-affiliates/artifacts/782g9583_WhatsApp%20Image%202025-08-16%20at%2015.39.47.jpeg";

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-6xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src={logoUrl} alt="AMT Logo" className="h-12 w-12 object-contain" />
            <span className="text-xl font-bold text-emerald-700">AMT Partners</span>
          </div>
          <div className="flex items-center space-x-6">
            <button onClick={() => navigate("/login")} className="text-emerald-700 hover:text-emerald-900 font-medium transition">
              Sign In
            </button>
            <button onClick={() => navigate("/apply")} className="px-5 py-2.5 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 font-medium transition hover:shadow-lg hover:shadow-emerald-200">
              Apply Now
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 relative bg-gradient-to-br from-emerald-50 via-emerald-50/50 to-white">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-300/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-200/40 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-40 right-1/4 w-48 h-48 bg-emerald-400/20 rounded-full blur-2xl"></div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-block px-4 py-1.5 bg-emerald-600 text-white rounded-full text-sm font-medium mb-6 animate-bounce" style={{animationDuration: '2s'}}>
            Earn up to 18% commission
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Turn Your Network Into
            <span className="block text-emerald-600">
              Passive Income
            </span>
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Join the AMT Partner Program and earn commissions every time someone you refer becomes a customer.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate("/apply")} 
              className="group px-8 py-4 bg-emerald-600 text-white text-lg font-semibold rounded-full hover:bg-emerald-700 transition-all hover:shadow-xl hover:shadow-emerald-300 hover:-translate-y-1"
            >
              Start Earning Today
              <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
            </button>
            <button 
              onClick={() => navigate("/login")} 
              className="px-8 py-4 bg-white text-emerald-700 text-lg font-semibold rounded-full border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all"
            >
              Partner Login
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6 bg-emerald-600">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "500+", label: "Active Partners" },
              { value: "$2M+", label: "Commissions Paid" },
              { value: "5", label: "Products" },
              { value: "24/7", label: "Dashboard Access" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-emerald-100 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Tiers */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Earn More As You Grow</h3>
            <p className="text-gray-500 text-lg">Your commission rate increases with your success</p>
          </div>
          
          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-emerald-300 via-emerald-500 to-emerald-700 -translate-y-1/2 rounded-full"></div>
            
            <div className="grid md:grid-cols-3 gap-8 md:gap-4">
              {[
                { tier: "Agent", range: "0-99 ARUs", rate: "7%", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", icon: "🚀" },
                { tier: "Silver", range: "100-199 ARUs", rate: "12%", bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-700", icon: "⭐" },
                { tier: "Gold", range: "200+ ARUs", rate: "18%", bg: "bg-emerald-600", border: "border-emerald-700", text: "text-white", icon: "👑" }
              ].map((item, i) => (
                <div key={i} className="relative group">
                  <div className={`relative ${item.bg} p-8 rounded-2xl border-2 ${item.border} transition-all hover:-translate-y-2 hover:shadow-xl cursor-default`}>
                    <div className={`absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 ${i === 2 ? 'bg-emerald-700' : 'bg-emerald-200'} rounded-full flex items-center justify-center text-xl z-10`}>
                      {item.icon}
                    </div>
                    <div className="text-center pt-4">
                      <h4 className={`text-xl font-bold ${i === 2 ? 'text-white' : 'text-gray-900'} mb-1`}>{item.tier}</h4>
                      <p className={`${i === 2 ? 'text-emerald-200' : 'text-gray-400'} text-sm mb-4`}>{item.range}</p>
                      <p className={`text-5xl font-bold ${item.text}`}>{item.rate}</p>
                      <p className={`${i === 2 ? 'text-emerald-200' : 'text-gray-400'} text-sm mt-1`}>commission</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-emerald-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h3>
            <p className="text-gray-500 text-lg">Four simple steps to start earning</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Apply", desc: "Fill out a quick application form" },
              { step: "02", title: "Get Approved", desc: "We review within 24-48 hours" },
              { step: "03", title: "Share Links", desc: "Get unique referral links for each product" },
              { step: "04", title: "Earn", desc: "Get paid for every successful referral" }
            ].map((item, i) => (
              <div key={i} className="group cursor-default">
                <div className="relative">
                  <span className="text-6xl font-bold text-emerald-200 group-hover:text-emerald-300 transition-colors">{item.step}</span>
                  <div className="absolute bottom-0 left-0">
                    <h4 className="text-lg font-bold text-emerald-700 group-hover:text-emerald-800 transition-colors">{item.title}</h4>
                    <p className="text-gray-500 text-sm mt-1">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 relative overflow-hidden bg-emerald-700">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 w-80 h-80 border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="max-w-3xl mx-auto text-center relative">
          <img src={logoUrl} alt="AMT Logo" className="h-20 w-20 object-contain mx-auto mb-6 rounded-xl bg-white/10 p-2" />
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Start Earning?</h3>
          <p className="text-emerald-100 text-lg mb-8">Join our growing community of partners today</p>
          <button 
            onClick={() => navigate("/apply")} 
            className="group px-8 py-4 bg-white text-emerald-700 text-lg font-semibold rounded-full hover:bg-emerald-50 transition-all hover:shadow-xl hover:-translate-y-1"
          >
            Apply Now — It's Free
            <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-emerald-900">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-3 mb-4 md:mb-0">
            <img src={logoUrl} alt="AMT Logo" className="h-10 w-10 object-contain rounded" />
            <p className="text-emerald-200 text-sm">© 2025 Africa Market Technologies</p>
          </div>
          <div className="flex space-x-6">
            <button onClick={() => navigate("/login")} className="text-emerald-300 hover:text-white text-sm transition">Partner Login</button>
            <button onClick={() => navigate("/apply")} className="text-emerald-300 hover:text-white text-sm transition">Apply</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============== APPLICATION FORM ==============
const ApplicationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    experience: "",
    audience: "",
    why_join: ""
  });

  const countries = [
    "Nigeria", "Ghana", "Senegal", "Côte d'Ivoire", "Mali", "Burkina Faso",
    "Niger", "Guinea", "Benin", "Togo", "Sierra Leone", "Liberia",
    "Gambia", "Guinea-Bissau", "Mauritania", "Cape Verde",
    "India", "Pakistan", "Bangladesh", "Indonesia", "Philippines", "Vietnam",
    "Thailand", "Malaysia", "Singapore", "Japan", "South Korea", "China",
    "Sri Lanka", "Nepal", "Myanmar", "Cambodia",
    "United States", "United Kingdom", "Canada", "Australia", "Germany", 
    "France", "Kenya", "South Africa", "Brazil", "Mexico", "UAE", "Other"
  ];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/applications", form);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for applying to the AMT Partner Program. We will review your application and get back to you within 2-3 business days.
          </p>
          <button onClick={() => navigate("/")} className="text-emerald-600 font-medium hover:text-emerald-700">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <button 
          onClick={() => navigate("/")} 
          className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent mb-2">
            Partner Application
          </h1>
          <p className="text-gray-600">Join the AMT Partner Program</p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="apply-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="apply-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="apply-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                <select
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="apply-country"
                >
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
              <select
                name="experience"
                value={form.experience}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Select Experience Level</option>
                <option value="none">No prior experience</option>
                <option value="beginner">Beginner (less than 1 year)</option>
                <option value="intermediate">Intermediate (1-3 years)</option>
                <option value="expert">Expert (3+ years)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">How will you promote AMT products?</label>
              <textarea
                name="audience"
                value={form.audience}
                onChange={handleChange}
                rows={3}
                placeholder="Describe your audience and promotion methods..."
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Why do you want to join?</label>
              <textarea
                name="why_join"
                value={form.why_join}
                onChange={handleChange}
                rows={3}
                placeholder="Tell us why you want to be an AMT Partner..."
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
              data-testid="apply-submit"
            >
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </form>

          <p className="text-center text-gray-500 text-sm mt-6">
            Already a partner? <button onClick={() => navigate("/login")} className="text-emerald-600 font-medium">Sign In</button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = ({ loginPath = "/login" }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Remembered so ResetPasswordPage (reached via the emailed link, which
    // carries no portal info) can send the user back to the login page
    // they started from instead of always defaulting to the agent one.
    try {
      sessionStorage.setItem("amt_post_reset_login_path", loginPath);
    } catch (e) {
      // ignore (e.g. private browsing)
    }
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
      toast.success("If your email is registered, you will receive a reset link shortly.");
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
      toast.success("If your email is registered, you will receive a reset link shortly.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent">
              AMT Partner
            </h1>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Check Your Email</h2>
            <p className="text-gray-500 mb-6">
              If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset link.
            </p>
            <p className="text-gray-400 text-sm mb-6">
              The link will expire in 30 minutes.
            </p>
            <button
              onClick={() => navigate(loginPath)}
              className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent">
            AMT Partner
          </h1>
          <p className="text-gray-500 mt-2">Reset your password</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter your email"
                required
                data-testid="forgot-email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition shadow-lg disabled:opacity-50"
              data-testid="forgot-submit"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={() => navigate(loginPath)} className="text-emerald-600 hover:text-emerald-700 font-medium">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // The emailed reset link carries no portal info, so fall back to
  // whichever login page ForgotPasswordPage was opened from in this
  // browser session (set in sessionStorage there); default to agent login.
  let loginPath = "/login";
  try {
    loginPath = sessionStorage.getItem("amt_post_reset_login_path") || "/login";
  } catch (e) {
    // ignore (e.g. private browsing)
  }
  const forgotPath = loginPath === "/amt778797/login" ? "/amt778797/forgot-password" : "/forgot-password";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) {
      setVerifying(false);
      return;
    }
    try {
      const response = await api.get(`/auth/verify-reset-token?token=${token}`);
      if (response.valid) {
        setTokenValid(true);
        setEmail(response.email);
      }
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl p-8 border border-red-100 shadow-xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Invalid or Expired Link</h2>
            <p className="text-gray-500 mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <button
              onClick={() => navigate(forgotPath)}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition"
            >
              Request New Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Password Reset Complete!</h2>
            <p className="text-gray-500 mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <button
              onClick={() => navigate(loginPath)}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent">
            AMT Partner
          </h1>
          <p className="text-gray-500 mt-2">Create your new password</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl">
          <div className="mb-6 p-3 bg-gray-50 rounded text-sm">
            <p className="text-gray-500">Email</p>
            <p className="text-gray-800 font-medium">{email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter new password"
                required
                minLength={8}
                data-testid="reset-password"
              />
              <p className="text-xs text-gray-400 mt-1">Must be at least 8 characters with letters and numbers</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Confirm new password"
                required
                data-testid="reset-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition shadow-lg disabled:opacity-50"
              data-testid="reset-submit"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Agent Login Page
const AgentLoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needs2FA, setNeeds2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [revealedCode, setRevealedCode] = useState(null);
  const [verifyCodeInput, setVerifyCodeInput] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, needs2FA ? totpCode : null);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      let resp = error.response?.data;
      // If response data is a string, try to parse JSON
      if (typeof resp === 'string') {
        try { resp = JSON.parse(resp); } catch (e) { /* ignore */ }
      }
      const msg = resp?.detail || resp?.message || error.response?.statusText || "Login failed";
      if (msg === "2FA code required") {
        setNeeds2FA(true);
        toast.info("Please enter your 2FA code");
      } else if (resp?.error === "email_not_verified" || error.response?.status === 403) {
        // show unverified banner with backend message when possible
        const message = resp?.message || msg || "Your email is not verified";
        setUnverified({ message, resend_endpoint: resp?.resend_endpoint || "/api/auth/resend-verification" });
        toast.error(message);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendFromLogin = async () => {
    if (!email) {
      toast.error("Enter your email above to resend verification");
      return;
    }
    setResendLoading(true);
    try {
      // Use POST JSON to request resend and ask to reveal code for convenience
      const resp = await api.post(`/auth/resend-verification`, { email, reveal_code: true });
      toast.success(resp.message || "Verification resent");
      if (resp.verification_code) {
        setRevealedCode(resp.verification_code);
      }
      setUnverified({ message: resp.message, resend_endpoint: "/api/auth/resend-verification" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend verification");
    } finally {
      setResendLoading(false);
    }
  };

  const handleInlineVerify = async () => {
    if (!verifyCodeInput || verifyCodeInput.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/auth/verify-email?email=${encodeURIComponent(email)}&code=${verifyCodeInput}`);
      localStorage.setItem("amt_token", response.access_token || response.access_token);
      localStorage.setItem("amt_user_type", "agent");
      // If response includes agent, set user via Auth context by reloading or redirect
      toast.success("Email verified! Redirecting...");
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent">
            AMT Partner
          </h1>
          <p className="text-gray-500 mt-2">Agent Portal</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="agent@example.com"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
            </div>

            {needs2FA && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">2FA Code</label>
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="123456"
                  maxLength={6}
                  data-testid="login-2fa"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition shadow-lg disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {unverified && (
            <div className="mt-4 bg-yellow-50 border border-yellow-100 p-4 rounded-lg text-sm">
              <p className="text-yellow-800 mb-2">{unverified.message}</p>
              <div className="flex items-center gap-2">
                <button onClick={handleResendFromLogin} disabled={resendLoading} className="px-3 py-2 text-sm text-emerald-700 bg-white border border-emerald-100 rounded hover:bg-emerald-50">
                  {resendLoading ? "Sending..." : "Resend verification email"}
                </button>
                <button onClick={() => navigate(`/verify-email?email=${encodeURIComponent(email)}`)} className="px-3 py-2 text-sm text-emerald-700 bg-white border border-emerald-100 rounded hover:bg-emerald-50">
                  Open verification page
                </button>
                {revealedCode && (
                  <div className="text-xs text-gray-600">Code: <strong className="ml-1">{revealedCode}</strong></div>
                )}
              </div>

              <div className="mt-3">
                <label className="block text-sm text-gray-700 mb-1">Enter verification code</label>
                <div className="flex gap-2">
                  <input value={verifyCodeInput} onChange={(e) => setVerifyCodeInput(e.target.value)} placeholder="123456" maxLength={6} className="px-3 py-2 border rounded w-32" />
                  <button onClick={handleInlineVerify} className="px-3 py-2 bg-emerald-600 text-white rounded">Verify</button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 text-center flex items-center justify-center gap-4">
            <button 
              onClick={() => navigate("/forgot-password")} 
              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
            <button
              onClick={() => navigate(`/verify-email?email=${encodeURIComponent(email)}`)}
              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
              data-testid="goto-verify-email"
            >
              Verify your email
            </button>
          </div>

          <div className="mt-4 text-center">
            <span className="text-gray-500">Don't have an account? </span>
            <button onClick={() => navigate("/register")} className="text-emerald-600 hover:text-emerald-700 font-medium" data-testid="goto-register">
              Register
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Setup Account Page (for invited partners)
const SetupAccountPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { setUser, setToken: setAuthToken } = useAuth();

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const data = await api.get(`/invitations/verify/${token}`);
      setInvitation(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired invitation");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Photo must be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setPhotoUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setSubmitting(true);
    try {
      const data = await api.post("/invitations/accept", { token, password, photo_url: photoUrl });
      toast.success("Account created successfully!");
      setAuthToken(data.access_token);
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("userType", "agent");
      setUser(data.agent);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">✗</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid Invitation</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <button 
            onClick={() => navigate("/login")} 
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded transition"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="text-emerald-500 text-5xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-800">Welcome, {invitation?.name}!</h1>
          <p className="text-gray-500 mt-2">Complete your profile to get started</p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
          <div className="mb-6 p-3 bg-gray-50 rounded text-sm">
            <p className="text-gray-500">Email</p>
            <p className="text-gray-800 font-medium">{invitation?.email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Profile Photo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo (Optional)</label>
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="photo-upload"
                    data-testid="photo-upload-input"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="inline-block px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg cursor-pointer transition"
                  >
                    Choose Photo
                  </label>
                  <p className="text-xs text-gray-400 mt-1">Max 2MB, JPG or PNG</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Create a password"
                required
                data-testid="setup-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Confirm your password"
                required
                data-testid="setup-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
              data-testid="setup-submit"
            >
              {submitting ? "Creating Account..." : "Complete Setup"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Agent Register Page
const AgentRegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("register"); // register, verify
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    password: "",
    confirmPassword: "",
    payout_method: "paypal",
    payout_details: ""
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setEmail(form.email);
      setStep("verify");
      toast.success("Check your email for verification code!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post(`/auth/verify-email?email=${encodeURIComponent(email)}&code=${verificationCode}`);
      localStorage.setItem("token", response.access_token);
      localStorage.setItem("userType", "agent");
      toast.success("Email verified! Welcome!");
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await api.post(`/auth/resend-verification?email=${encodeURIComponent(email)}`);
      toast.success("New code sent!");
    } catch (error) {
      toast.error("Failed to resend code");
    }
  };

  if (step === "verify") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent">AMT Partner</h1>
            <p className="text-gray-500 mt-2">Verify your email</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-gray-600">We sent a code to <strong>{email}</strong></p>
            </div>
            <form onSubmit={handleVerify} className="space-y-4">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                className="w-full px-4 py-4 text-center text-2xl tracking-widest bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                maxLength={6}
                data-testid="verification-code"
              />
              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
                data-testid="verify-submit"
              >
                {loading ? "Verifying..." : "Verify Email"}
              </button>
            </form>
            <button onClick={handleResend} className="w-full mt-4 text-emerald-600 hover:text-emerald-700 text-sm">
              Resend Code
            </button>
          </div>
        </div>
      </div>
    );
  }

  const countries = [
    "Nigeria", "Ghana", "Senegal", "Côte d'Ivoire", "Mali", "Burkina Faso",
    "Niger", "Guinea", "Benin", "Togo", "Sierra Leone", "Liberia",
    "Gambia", "Guinea-Bissau", "Mauritania", "Cape Verde",
    "India", "Pakistan", "Bangladesh", "Indonesia", "Philippines", "Vietnam",
    "Thailand", "Malaysia", "Singapore", "Japan", "South Korea", "China",
    "Sri Lanka", "Nepal", "Myanmar", "Cambodia",
    "United States", "United Kingdom", "Canada", "Australia", "Germany", 
    "France", "Kenya", "South Africa", "Brazil", "Mexico", "UAE", "Other"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-500 bg-clip-text text-transparent">
            AMT Partner
          </h1>
          <p className="text-gray-500 mt-2">Create your agent account</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  data-testid="register-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  data-testid="register-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
                data-testid="register-country"
              >
                <option value="">Select country</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payout Method</label>
                <select
                  name="payout_method"
                  value={form.payout_method}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="register-payout-method"
                >
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {form.payout_method === "paypal" && "PayPal Email"}
                  {form.payout_method === "bank_transfer" && "Bank Account Details"}
                  {form.payout_method === "mobile_money" && "Mobile Money Number"}
                </label>
                <input
                  type="text"
                  name="payout_details"
                  value={form.payout_details}
                  onChange={handleChange}
                  placeholder={
                    form.payout_method === "paypal" ? "your@email.com" :
                    form.payout_method === "bank_transfer" ? "Bank Name, Account Number" :
                    "Phone number (e.g., +233...)"
                  }
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="register-payout-details"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition shadow-lg disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-gray-500">Already have an account? </span>
            <button onClick={() => navigate("/login")} className="text-emerald-600 hover:text-emerald-700 font-medium" data-testid="goto-login">
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Agent Dashboard Page
const AgentDashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, analyticsData, tierData] = await Promise.all([
        api.get("/dashboard/stats", token),
        api.get("/dashboard/analytics?days=30", token),
        api.get("/partner/tier-info", token)
      ]);
      setStats(statsData);
      setAnalytics(analyticsData);
      setTierInfo(tierData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch(tier) {
      case 'gold': return 'from-yellow-400 to-amber-500';
      case 'silver': return 'from-gray-300 to-gray-400';
      default: return 'from-emerald-400 to-emerald-500';
    }
  };

  const getTierBadgeColor = (tier) => {
    switch(tier) {
      case 'gold': return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
      case 'silver': return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
      default: return 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header with Tier Badge */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Welcome back, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-gray-500 mt-1">Here's your performance overview</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className={`px-4 py-2 rounded-lg font-semibold ${getTierBadgeColor(tierInfo?.tier)}`}>
            {tierInfo?.tier_display || 'Agent'} • {tierInfo?.commission_rate || 7}%
          </div>
          <div className="bg-emerald-50 px-4 py-2 rounded-lg border border-emerald-200">
            <span className="text-gray-500 text-sm">Code: </span>
            <span className="text-emerald-600 font-mono font-bold">{user?.referral_code}</span>
          </div>
        </div>
      </div>

      {/* Tier Progress Card */}
      <div className={`bg-gradient-to-r ${getTierColor(tierInfo?.tier)} rounded-xl p-6 shadow-lg text-white`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">{tierInfo?.tier_display || 'Agent'}</h2>
            <p className="text-white/80">Your current partner tier</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold">{tierInfo?.aru_count || 0}</p>
            <p className="text-white/80">Active Revenue Units</p>
          </div>
        </div>
        
        {/* Progress to Next Tier */}
        {tierInfo?.tier !== 'gold' && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Progress to {tierInfo?.tier_progress?.next_tier}</span>
              <span>{tierInfo?.tier_progress?.arus_needed} ARUs needed</span>
            </div>
            <div className="h-3 bg-white/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${tierInfo?.tier_progress?.progress_percent || 0}%` }}
              ></div>
            </div>
          </div>
        )}
        
        {tierInfo?.tier === 'gold' && (
          <div className="mt-4 flex items-center space-x-2">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold">Maximum tier achieved! Earning 18% commission.</span>
          </div>
        )}

        {/* Tier Thresholds */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className={`p-3 rounded-lg ${tierInfo?.tier === 'agent' ? 'bg-white/30' : 'bg-white/10'}`}>
            <p className="font-semibold">Agent</p>
            <p className="text-sm text-white/80">0-99 ARUs • 7%</p>
          </div>
          <div className={`p-3 rounded-lg ${tierInfo?.tier === 'silver' ? 'bg-white/30' : 'bg-white/10'}`}>
            <p className="font-semibold">Silver Partner</p>
            <p className="text-sm text-white/80">100-199 ARUs • 12%</p>
          </div>
          <div className={`p-3 rounded-lg ${tierInfo?.tier === 'gold' ? 'bg-white/30' : 'bg-white/10'}`}>
            <p className="font-semibold">Gold Partner</p>
            <p className="text-sm text-white/80">200+ ARUs • 18%</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Active ARUs"
          value={tierInfo?.aru_count || 0}
          subtitle="Paying customers"
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
          color="emerald"
        />
        <StatCard
          title="Total Earnings"
          value={`$${tierInfo?.wallet?.total_earnings?.toFixed(2) || "0.00"}`}
          subtitle={`$${tierInfo?.wallet?.pending?.toFixed(2) || "0.00"} pending`}
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          color="gold"
        />
        <StatCard
          title="Available"
          value={`$${tierInfo?.wallet?.balance?.toFixed(2) || "0.00"}`}
          subtitle="Ready to withdraw"
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>}
          color="green"
        />
        <StatCard
          title="Commission Rate"
          value={`${tierInfo?.commission_rate || 7}%`}
          subtitle={tierInfo?.tier_display || 'Agent'}
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
          color="orange"
        />
      </div>

      {/* Wallet Summary */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Wallet Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-amber-600 text-sm font-medium">Pending (30-day hold)</p>
            <p className="text-2xl font-bold text-amber-700">${tierInfo?.wallet?.pending?.toFixed(2) || "0.00"}</p>
          </div>
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-emerald-600 text-sm font-medium">Available Balance</p>
            <p className="text-2xl font-bold text-emerald-700">${tierInfo?.wallet?.balance?.toFixed(2) || "0.00"}</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-blue-600 text-sm font-medium">Total Paid Out</p>
            <p className="text-2xl font-bold text-blue-700">${tierInfo?.wallet?.paid?.toFixed(2) || "0.00"}</p>
          </div>
        </div>
      </div>

      {/* Earnings by Product & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Earnings by Product</h2>
          {analytics?.earnings_by_product?.length > 0 ? (
            <div className="space-y-4">
              {analytics.earnings_by_product.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      ["bg-emerald-500", "bg-amber-500", "bg-green-500", "bg-teal-500"][idx % 4]
                    }`}></div>
                    <span className="text-gray-700">{item._id}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-800 font-semibold">${item.total_earnings?.toFixed(2)}</span>
                    <span className="text-gray-400 text-sm ml-2">({item.count} referrals)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No earnings data yet. Start referring to see your progress!</p>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Activity</h2>
          {analytics?.referral_trends?.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {analytics.referral_trends.slice(-10).reverse().map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 text-sm">{item._id}</span>
                  <div className="flex items-center space-x-4">
                    <span className="text-emerald-600">{item.count} referrals</span>
                    <span className="text-amber-600">${item.revenue?.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Agent Referrals Page
const AgentReferralsPage = () => {
  const { user, token } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (user && token) {
      fetchReferrals();
    }
  }, [filter, user, token]);

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      if (!user || !token) return;
      let url = `/agents/${user.id}/referrals`;
      if (filter !== "all") {
        url += `?status=${filter}`;
      }
      const data = await api.get(url, token);
      setReferrals(data);
    } catch (error) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
    refunded: "bg-orange-100 text-orange-700 border-orange-200"
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Referrals</h1>
        <div className="flex items-center space-x-2">
          {["all", "active", "pending", "cancelled"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === f 
                  ? "bg-emerald-500 text-white" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-gray-400 mt-4">No referrals found</p>
            <p className="text-gray-300 text-sm mt-2">Share your referral links to start earning!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Customer</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Product</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Plan</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Amount</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {referrals.map(ref => (
                  <tr key={ref.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-gray-800 font-medium">{ref.customer_name || "Anonymous"}</p>
                        <p className="text-gray-400 text-sm">{ref.customer_email || ref.customer_id}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{ref.product}</td>
                    <td className="px-6 py-4 text-gray-600">{ref.plan}</td>
                    <td className="px-6 py-4 text-amber-600 font-semibold">${ref.amount?.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[ref.status]}`}>
                        {ref.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Agent Commissions Page
const AgentCommissionsPage = () => {
  const { user, token } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchCommissions();
  }, [filter]);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      let url = `/agents/${user.id}/commissions`;
      if (filter !== "all") {
        url += `?status=${filter}`;
      }
      const data = await api.get(url, token);
      setCommissions(data);
    } catch (error) {
      toast.error("Failed to load commissions");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    available: "bg-emerald-100 text-emerald-700 border-emerald-200",
    paid: "bg-blue-100 text-blue-700 border-blue-200",
    cancelled: "bg-red-100 text-red-700 border-red-200"
  };

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Commissions</h1>
        <div className="flex items-center space-x-2">
          {["all", "pending", "available", "paid"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === f 
                  ? "bg-emerald-500 text-white" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : commissions.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-400 mt-4">No commissions yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Product</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Plan</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Referral Amount</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Rate</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Earned</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {commissions.map(comm => (
                  <tr key={comm.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-gray-800 font-medium">{comm.product}</td>
                    <td className="px-6 py-4 text-gray-600">{comm.plan}</td>
                    <td className="px-6 py-4 text-gray-600">${comm.referral_amount?.toFixed(2)}</td>
                    <td className="px-6 py-4 text-emerald-600">
                      {comm.commission_type === "percentage" ? `${comm.commission_value}%` : `$${comm.commission_value}`}
                    </td>
                    <td className="px-6 py-4 text-amber-600 font-semibold">${comm.commission_earned?.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[comm.status]}`}>
                        {comm.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(comm.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Agent Wallet Page
const AgentWalletPage = () => {
  const { user, token } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: "",
    method: user?.payout_method || "paypal",
    details: user?.payout_details || ""
  });
  const [payoutLoading, setPayoutLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletData, payoutsData] = await Promise.all([
        api.get("/wallet", token),
        api.get("/payouts", token)
      ]);
      setWallet(walletData);
      setPayouts(payoutsData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayout = async (e) => {
    e.preventDefault();
    setPayoutLoading(true);
    try {
      await api.post(`/agents/${user.id}/payout-request`, payoutForm, token);
      toast.success("Payout request submitted!");
      setShowPayoutModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payout request failed");
    } finally {
      setPayoutLoading(false);
    }
  };

  const statusColors = {
    pending: "bg-amber-100 text-amber-700",
    processing: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Wallet</h1>
        <button
          onClick={() => setShowPayoutModal(true)}
          disabled={!wallet?.available || wallet.available < 10}
          className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="request-payout-btn"
        >
          Request Payout
        </button>
      </div>

      {/* Wallet Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 shadow-lg">
          <p className="text-emerald-100 text-sm">Total Earnings</p>
          <p className="text-4xl font-bold text-white mt-2">${wallet?.total?.toFixed(2) || "0.00"}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <p className="text-gray-500 text-sm">Pending</p>
          <p className="text-3xl font-bold text-amber-500 mt-2">${wallet?.pending?.toFixed(2) || "0.00"}</p>
          <p className="text-gray-400 text-xs mt-1">30-day hold period</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <p className="text-gray-500 text-sm">Available</p>
          <p className="text-3xl font-bold text-emerald-500 mt-2">${wallet?.available?.toFixed(2) || "0.00"}</p>
          <p className="text-gray-400 text-xs mt-1">Ready to withdraw</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <p className="text-gray-500 text-sm">Paid Out</p>
          <p className="text-3xl font-bold text-blue-500 mt-2">${wallet?.paid?.toFixed(2) || "0.00"}</p>
          <p className="text-gray-400 text-xs mt-1">Lifetime withdrawals</p>
        </div>
      </div>

      {/* Payout History */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">Payout History</h2>
        </div>
        {payouts.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <p className="text-gray-400 mt-4">No payouts yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Date</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Amount</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Method</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payouts.map(payout => (
                  <tr key={payout.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-amber-600 font-semibold">
                      ${payout.amount?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-gray-600 capitalize">
                      {payout.method?.replace("_", " ")}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[payout.status]}`}>
                        {payout.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Request Payout</h2>
            <form onSubmit={handlePayout} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (USD)</label>
                <input
                  type="number"
                  value={payoutForm.amount}
                  onChange={(e) => setPayoutForm({...payoutForm, amount: parseFloat(e.target.value)})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  min="10"
                  max={wallet?.available || 0}
                  step="0.01"
                  required
                  data-testid="payout-amount"
                />
                <p className="text-gray-400 text-xs mt-1">Min: $10 | Available: ${wallet?.available?.toFixed(2)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payout Method</label>
                <select
                  value={payoutForm.method}
                  onChange={(e) => setPayoutForm({...payoutForm, method: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="payout-method"
                >
                  <option value="paypal">PayPal</option>
                  <option value="stripe">Stripe</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payout Details</label>
                <input
                  type="text"
                  value={payoutForm.details}
                  onChange={(e) => setPayoutForm({...payoutForm, details: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="email@paypal.com"
                  data-testid="payout-details"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-700 text-sm">
                  ⚠️ This is a <strong>MOCK</strong> payout system for MVP. No real payments will be processed.
                </p>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPayoutModal(false)}
                  className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={payoutLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
                  data-testid="submit-payout"
                >
                  {payoutLoading ? "Processing..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Agent Links Page
const AgentLinksPage = () => {
  const { token, user } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const data = await api.get("/referral-links", token);
      setLinks(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const commissionRate = user?.commission_rate || 7;

  return (
    <div className="space-y-6" data-testid="links-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">My Referral Links</h1>
        <p className="text-gray-500 mt-2">Share these links to earn commissions on every signup</p>
      </div>

      {links.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <p className="text-amber-800 font-medium">No products available yet</p>
          <p className="text-amber-600 text-sm mt-1">Products are being configured. Check back soon!</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {links.map(link => (
            <div key={link.product_id} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">{link.product}</h2>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                  {commissionRate}% Commission
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Referral Link</label>
                  {link.signup_url_configured ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={link.referral_link}
                        readOnly
                        className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 font-mono text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(link.referral_link)}
                        className="px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition"
                        data-testid={`copy-link-${link.product_id}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                      Link coming soon - product signup URL being configured
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Referral Code</label>
                  <div className="flex items-center space-x-2">
                    <span className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 font-mono font-bold">
                      {link.referral_code}
                    </span>
                    <button
                      onClick={() => copyToClipboard(link.referral_code)}
                      className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                      data-testid={`copy-code-${link.product_id}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Customers can enter this code during signup if the link doesn't work</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Agent AI Recommendations Page
const AgentRecommendationsPage = () => {
  const { token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const data = await api.get("/recommendations", token);
      setRecommendations(data.recommendations || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mx-auto"></div>
          <p className="text-gray-500 mt-4">AI is analyzing your performance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recommendations-page">
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">AI Insights</h1>
          <p className="text-gray-500">Personalized recommendations to maximize your earnings</p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 rounded-xl p-6">
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-emerald-800 font-semibold">How it works</h3>
            <p className="text-gray-600 text-sm mt-1">
              Our AI analyzes your referral history, conversion rates, and market trends to suggest the best products for you to promote.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        {recommendations.map((rec, idx) => (
          <div 
            key={idx} 
            className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h2 className="text-2xl font-bold text-gray-800">{rec.product}</h2>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    rec.confidence >= 0.8 
                      ? "bg-emerald-100 text-emerald-700" 
                      : rec.confidence >= 0.7 
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-600"
                  }`}>
                    {Math.round(rec.confidence * 100)}% confidence
                  </span>
                </div>
                <p className="text-gray-500 mt-3">{rec.reason}</p>
                <div className="mt-4">
                  <span className="text-gray-400 text-sm">Potential Earnings</span>
                  <p className="text-amber-600 font-bold text-lg">{rec.potential_earnings}</p>
                </div>
              </div>
              <div className="ml-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-lg ${
                  idx === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500" :
                  idx === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400" :
                  "bg-gradient-to-br from-amber-600 to-amber-700"
                }`}>
                  <span className="text-2xl font-bold text-white">#{idx + 1}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={fetchRecommendations}
        className="w-full py-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 rounded-xl transition flex items-center justify-center space-x-2"
        data-testid="refresh-recommendations"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Refresh Recommendations</span>
      </button>
    </div>
  );
};

// Agent Profile Page
const AgentProfilePage = () => {
  const { user, token, updateUser } = useAuth();
  const [qrImage, setQrImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    country: user?.country || "",
    payout_method: user?.payout_method || "paypal",
    payout_details: user?.payout_details || ""
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updated = await api.put("/profile", form, token);
      updateUser(updated);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const enable2FA = async () => {
    setTwoFaLoading(true);
    try {
      const data = await api.post("/auth/2fa/enable", {}, token);
      setTwoFaSetup(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enable 2FA");
    } finally {
      setTwoFaLoading(false);
    }
  };

  useEffect(() => {
    const generateQr = async () => {
      if (!twoFaSetup?.qr_uri) {
        setQrImage(null);
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(twoFaSetup.qr_uri);
        setQrImage(dataUrl);
      } catch (e) {
        setQrImage(null);
      }
    };
    generateQr();
  }, [twoFaSetup]);

  const verify2FA = async () => {
    setTwoFaLoading(true);
    try {
      await api.post(`/auth/2fa/verify?code=${verifyCode}`, {}, token);
      toast.success("2FA enabled successfully!");
      updateUser({ two_fa_enabled: true });
      setTwoFaSetup(null);
      setVerifyCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const disable2FA = async () => {
    const code = prompt("Enter your 2FA code to disable:");
    if (!code) return;
    
    setTwoFaLoading(true);
    try {
      await api.post(`/auth/2fa/disable?code=${code}`, {}, token);
      toast.success("2FA disabled");
      updateUser({ two_fa_enabled: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const countries = [
    "Nigeria", "Ghana", "Senegal", "Côte d'Ivoire", "Mali", "Burkina Faso",
    "Niger", "Guinea", "Benin", "Togo", "Sierra Leone", "Liberia",
    "Gambia", "Guinea-Bissau", "Mauritania", "Cape Verde",
    "India", "Pakistan", "Bangladesh", "Indonesia", "Philippines", "Vietnam",
    "Thailand", "Malaysia", "Singapore", "Japan", "South Korea", "China",
    "Sri Lanka", "Nepal", "Myanmar", "Cambodia",
    "United States", "United Kingdom", "Canada", "Australia", "Germany", 
    "France", "Kenya", "South Africa", "Brazil", "Mexico", "UAE", "Other"
  ];

  return (
    <div className="space-y-6" data-testid="profile-page">
      <h1 className="text-3xl font-bold text-gray-800">Profile Settings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Personal Information</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="profile-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="profile-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={user?.email}
                disabled
                className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-400 cursor-not-allowed"
              />
              <p className="text-gray-400 text-xs mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="profile-country"
              >
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payout Method</label>
                <select
                  name="payout_method"
                  value={form.payout_method}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="profile-payout-method"
                >
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {form.payout_method === "paypal" && "PayPal Email"}
                  {form.payout_method === "bank_transfer" && "Bank Account Details"}
                  {form.payout_method === "mobile_money" && "Mobile Money Number"}
                </label>
                <input
                  type="text"
                  name="payout_details"
                  value={form.payout_details}
                  onChange={handleChange}
                  placeholder={
                    form.payout_method === "paypal" ? "your@email.com" :
                    form.payout_method === "bank_transfer" ? "Bank Name, Account Number, Routing" :
                    "Phone number (e.g., +233...)"
                  }
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="profile-payout-details"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition shadow-lg disabled:opacity-50"
              data-testid="save-profile"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>

        {/* Security */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Security</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-800 font-medium">Two-Factor Authentication</p>
                  <p className="text-gray-400 text-sm">Add extra security</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  user?.two_fa_enabled 
                    ? "bg-emerald-100 text-emerald-700" 
                    : "bg-gray-100 text-gray-500"
                }`}>
                  {user?.two_fa_enabled ? "Enabled" : "Disabled"}
                </span>
              </div>

              {!user?.two_fa_enabled && !twoFaSetup && (
                <button
                  onClick={enable2FA}
                  disabled={twoFaLoading}
                  className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition disabled:opacity-50"
                  data-testid="enable-2fa"
                >
                  {twoFaLoading ? "Loading..." : "Enable 2FA"}
                </button>
              )}

              {twoFaSetup && (
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-2">Scan with authenticator or use secret:</p>
                    {qrImage ? (
                      <div className="flex items-center space-x-4">
                        <img src={qrImage} alt="2FA QR" className="w-40 h-40 bg-white p-2 rounded" />
                        <code className="text-emerald-600 text-xs break-all">{twoFaSetup.secret}</code>
                      </div>
                    ) : (
                      <code className="text-emerald-600 text-xs break-all">{twoFaSetup.secret}</code>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Verification Code</label>
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="123456"
                      maxLength={6}
                      data-testid="2fa-verify-code"
                    />
                  </div>
                  <button
                    onClick={verify2FA}
                    disabled={twoFaLoading || verifyCode.length !== 6}
                    className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition disabled:opacity-50"
                    data-testid="verify-2fa"
                  >
                    {twoFaLoading ? "Verifying..." : "Verify & Enable"}
                  </button>
                </div>
              )}

              {user?.two_fa_enabled && (
                <button
                  onClick={disable2FA}
                  disabled={twoFaLoading}
                  className="w-full py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition disabled:opacity-50"
                  data-testid="disable-2fa"
                >
                  Disable 2FA
                </button>
              )}
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Account</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Referral Code</span>
                <span className="text-emerald-600 font-mono font-bold">{user?.referral_code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Member Since</span>
                <span className="text-gray-700">{new Date(user?.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className="text-emerald-600">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== ADMIN PAGES ==============

// Admin Login Page
const AdminLoginPage = () => {
  const { adminLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success("Welcome, Admin!");
      navigate("/amt778797/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
            AMT Partner
          </h1>
          <p className="text-amber-600 mt-2 font-semibold">Admin Portal</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-amber-200 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="admin@amt.com"
                required
                data-testid="admin-login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="••••••••"
                required
                data-testid="admin-login-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold rounded-lg transition shadow-lg disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/amt778797/forgot-password")}
              className="text-amber-600 hover:text-amber-700 text-sm font-medium"
              data-testid="admin-forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboardPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await api.get("/amt778797/stats", token);
      setStats(data);
    } catch (error) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    switch(tier) {
      case 'gold': return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
      case 'silver': return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
      default: return 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white';
    }
  };

  const getTierLabel = (tier) => {
    switch(tier) {
      case 'gold': return 'Gold Partner';
      case 'silver': return 'Silver Partner';
      default: return 'Agent';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Partners"
          value={stats?.agents?.total || 0}
          subtitle={`${stats?.agents?.active || 0} active`}
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
          color="emerald"
        />
        <StatCard
          title="Total ARUs"
          value={stats?.tiers?.total_arus || 0}
          subtitle="Active Revenue Units"
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
          color="green"
        />
        <StatCard
          title="Total Revenue"
          value={`$${stats?.financials?.total_revenue?.toFixed(2) || "0.00"}`}
          subtitle="All time"
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          color="gold"
        />
        <StatCard
          title="Pending Payouts"
          value={`$${stats?.financials?.pending_payouts?.toFixed(2) || "0.00"}`}
          subtitle={`${stats?.financials?.pending_payout_count || 0} requests`}
          icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
          color="orange"
        />
      </div>

      {/* Partner Tier Distribution */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Partner Tier Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Agent Tier */}
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center justify-between mb-2">
              <span className="px-3 py-1 bg-gradient-to-r from-emerald-400 to-emerald-500 text-white text-sm font-semibold rounded-full">
                Agent
              </span>
              <span className="text-emerald-700 text-sm">7% commission</span>
            </div>
            <p className="text-3xl font-bold text-emerald-700">{stats?.tiers?.agent || 0}</p>
            <p className="text-emerald-600 text-sm">0-99 ARUs</p>
          </div>
          
          {/* Silver Tier */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="px-3 py-1 bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800 text-sm font-semibold rounded-full">
                Silver Partner
              </span>
              <span className="text-gray-700 text-sm">12% commission</span>
            </div>
            <p className="text-3xl font-bold text-gray-700">{stats?.tiers?.silver || 0}</p>
            <p className="text-gray-600 text-sm">100-199 ARUs</p>
          </div>
          
          {/* Gold Tier */}
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center justify-between mb-2">
              <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-sm font-semibold rounded-full">
                Gold Partner
              </span>
              <span className="text-amber-700 text-sm">18% commission</span>
            </div>
            <p className="text-3xl font-bold text-amber-700">{stats?.tiers?.gold || 0}</p>
            <p className="text-amber-600 text-sm">200+ ARUs</p>
          </div>
        </div>
      </div>

      {/* Top Agents & Revenue by Product */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Top Performing Partners</h2>
          {stats?.top_agents?.length > 0 ? (
            <div className="space-y-4">
              {stats.top_agents.map((agent, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow ${
                      idx === 0 ? "bg-yellow-400" : idx === 1 ? "bg-gray-300" : "bg-amber-600"
                    }`}>
                      <span className="text-white font-bold text-sm">{idx + 1}</span>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="text-gray-800 font-medium">{agent.name}</p>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getTierBadge(agent.tier)}`}>
                          {getTierLabel(agent.tier)}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs">{agent.aru_count || 0} ARUs • {agent.commission_rate || 7}%</p>
                    </div>
                  </div>
                  <span className="text-amber-600 font-bold">${agent.total_earnings?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No partner data yet</p>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Revenue by Product</h2>
          {stats?.by_product?.length > 0 ? (
            <div className="space-y-4">
              {stats.by_product.map((product, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      ["bg-emerald-500", "bg-amber-500", "bg-green-500", "bg-teal-500"][idx % 4]
                    }`}></div>
                    <span className="text-gray-700">{product._id}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-800 font-semibold">${product.revenue?.toFixed(2)}</span>
                    <span className="text-gray-400 text-sm ml-2">({product.count} referrals)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No product data yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Applications Page
const AdminApplicationsPage = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const fetchApplications = async () => {
    if (!token) return;
    try {
      const url = filter ? `/amt778797/applications?status=${filter}` : "/amt778797/applications";
      const data = await api.get(url, token);
      setApplications(data.applications || []);
    } catch (error) {
      console.error("Error loading applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filter, token]);

  const handleApprove = async (id) => {
    try {
      await api.post(`/amt778797/applications/${id}/approve`, {}, token);
      toast.success("Application approved! Invitation email sent.");
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Are you sure you want to reject this application?")) return;
    try {
      await api.post(`/amt778797/applications/${id}/reject`, {}, token);
      toast.success("Application rejected");
      fetchApplications();
    } catch (error) {
      toast.error("Failed to reject");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Partner Applications</h1>
        <div className="flex space-x-2">
          {["pending", "approved", "rejected", ""].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === s ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No applications found</div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div key={app.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{app.name}</h3>
                    <p className="text-gray-500 text-sm">{app.email} • {app.phone}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      app.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                      app.status === "approved" ? "bg-green-100 text-green-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {app.status}
                    </span>
                    <span className="text-gray-400 text-sm">{new Date(app.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-400 uppercase mb-1">Country</p>
                    <p className="text-gray-800">{app.country}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-400 uppercase mb-1">Experience</p>
                    <p className="text-gray-800">{app.experience || "Not specified"}</p>
                  </div>
                </div>

                {app.audience && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-xs font-medium text-gray-400 uppercase mb-1">How will they promote?</p>
                    <p className="text-gray-800">{app.audience}</p>
                  </div>
                )}

                {app.why_join && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-xs font-medium text-gray-400 uppercase mb-1">Why do they want to join?</p>
                    <p className="text-gray-800">{app.why_join}</p>
                  </div>
                )}

                {app.status === "pending" && (
                  <div className="flex space-x-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleApprove(app.id)}
                      className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition"
                    >
                      Approve & Send Invite
                    </button>
                    <button
                      onClick={() => handleReject(app.id)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Agents Page
const AdminAgentsPage = () => {
  const { token } = useAuth();
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", phone: "", country: "" });
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetailLoading, setAgentDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchAgents();
  }, [page, search]);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * 20, limit: 20 });
      if (search) params.append("search", search);
      const data = await api.get(`/amt778797/agents?${params}`, token);
      setAgents(data.agents);
      setTotal(data.total);
    } catch (error) {
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentDetail = async (agentId) => {
    setAgentDetailLoading(true);
    try {
      const data = await api.get(`/amt778797/agents/${agentId}`, token);
      setSelectedAgent(data);
    } catch (error) {
      toast.error("Failed to load partner details");
    } finally {
      setAgentDetailLoading(false);
    }
  };

  const invitePartner = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error("Name and email are required");
      return;
    }
    setInviting(true);
    try {
      const result = await api.post("/amt778797/partners/invite", inviteForm, token);
      setInviteSuccess(result);
      toast.success("Invitation sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const toggleAgentStatus = async (agentId, isActive) => {
    try {
      await api.put(`/amt778797/agents/${agentId}/status?is_active=${!isActive}`, {}, token);
      toast.success(`Partner ${!isActive ? "activated" : "deactivated"}`);
      fetchAgents();
      if (selectedAgent?.id === agentId) {
        setSelectedAgent(prev => ({ ...prev, is_active: !isActive }));
      }
    } catch (error) {
      toast.error("Failed to update partner status");
    }
  };

  const resendInvitation = async (agentId) => {
    setActionLoading("resend");
    try {
      await api.post(`/amt778797/partners/${agentId}/resend-invitation`, {}, token);
      toast.success("Invitation resent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    } finally {
      setActionLoading(null);
    }
  };

  const resetPartnerPassword = async (agentId) => {
    setActionLoading("reset");
    try {
      await api.post(`/amt778797/partners/${agentId}/reset-password`, {}, token);
      toast.success("Password reset email sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send password reset");
    } finally {
      setActionLoading(null);
    }
  };

  const getTierBadge = (tier) => {
    switch(tier) {
      case 'gold': return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
      case 'silver': return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
      default: return 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white';
    }
  };

  const getTierLabel = (tier) => {
    switch(tier) {
      case 'gold': return 'Gold';
      case 'silver': return 'Silver';
      default: return 'Agent';
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-agents">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Manage Partners</h1>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search partners..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-white border border-gray-200 rounded text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded transition"
          >
            + Invite Partner
          </button>
        </div>
      </div>

      {/* Partners Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      ) : agents.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg">No partners yet</p>
          <p className="text-sm">Invite your first partner to get started</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">ARUs</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Referral Code</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="text-right py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {agents.map(agent => (
              <tr key={agent.id} className="hover:bg-gray-50">
                <td className="py-4">
                  <button 
                    onClick={() => fetchAgentDetail(agent.id)}
                    className="text-left hover:text-emerald-600 transition"
                    data-testid={`view-agent-${agent.id}`}
                  >
                    <p className="text-gray-900 font-medium hover:underline">{agent.name}</p>
                    <p className="text-gray-400 text-xs">{agent.email}</p>
                  </button>
                </td>
                <td className="py-4">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${getTierBadge(agent.tier)}`}>
                    {getTierLabel(agent.tier)}
                  </span>
                </td>
                <td className="py-4 text-gray-600">{agent.aru_count || 0}</td>
                <td className="py-4">
                  <span className="text-emerald-600 font-mono text-sm">{agent.referral_code}</span>
                </td>
                <td className="py-4 text-gray-600 text-sm">{agent.country || "-"}</td>
                <td className="py-4">
                  <span className={`text-xs font-medium ${agent.is_active ? "text-emerald-600" : "text-red-600"}`}>
                    {agent.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="py-4 text-right space-x-2">
                  <button
                    onClick={() => fetchAgentDetail(agent.id)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                    data-testid={`view-btn-${agent.id}`}
                  >
                    View
                  </button>
                  <button
                    onClick={() => toggleAgentStatus(agent.id, agent.is_active)}
                    className={`text-xs ${agent.is_active ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}
                  >
                    {agent.is_active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
                ))}
              </tbody>
            </table>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            ← Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page + 1} of {Math.ceil(total / 20)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * 20 >= total}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            Next →
          </button>
        </div>
      )}

      {/* Invite Partner Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            {!inviteSuccess ? (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite Partner</h2>
                <p className="text-gray-500 text-sm mb-4">Send an email invitation to a new partner. They'll receive a link to set up their account.</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                      placeholder="John Doe"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                      placeholder="john@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={inviteForm.phone}
                        onChange={(e) => setInviteForm({...inviteForm, phone: e.target.value})}
                        placeholder="+1234567890"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        value={inviteForm.country}
                        onChange={(e) => setInviteForm({...inviteForm, country: e.target.value})}
                        placeholder="United States"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button onClick={() => { setShowInviteModal(false); setInviteForm({ name: "", email: "", phone: "", country: "" }); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                  <button
                    onClick={invitePartner}
                    disabled={inviting}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded disabled:opacity-50"
                  >
                    {inviting ? "Sending..." : "Send Invitation"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-emerald-600 text-xl">✓</span>
                  <h2 className="text-lg font-semibold text-gray-900">Invitation Sent</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  An invitation email has been sent to <strong>{inviteSuccess.email}</strong>.
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm mb-4">
                  <p className="text-gray-500 mb-1">Setup Link (expires in 7 days):</p>
                  <code className="text-xs text-gray-700 break-all">{inviteSuccess.setup_url}</code>
                </div>
                <p className="text-gray-500 text-sm mb-4">
                  Note: Check the console/logs for the email content (email integration pending).
                </p>
                <div className="flex justify-end">
                  <button 
                    onClick={() => { setShowInviteModal(false); setInviteSuccess(null); setInviteForm({ name: "", email: "", phone: "", country: "" }); fetchAgents(); }} 
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Partner Detail Modal */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="partner-detail-modal">
            {agentDetailLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500"></div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center overflow-hidden">
                      {selectedAgent.photo_url ? (
                        <img src={selectedAgent.photo_url} alt={selectedAgent.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl text-gray-400">{selectedAgent.name?.charAt(0)?.toUpperCase() || "P"}</span>
                      )}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{selectedAgent.name}</h2>
                      <p className="text-gray-500">{selectedAgent.email}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded ${getTierBadge(selectedAgent.tier)}`}>
                        {getTierLabel(selectedAgent.tier)} Partner
                      </span>
                    </div>
                  </div>
                  <button onClick={() => setSelectedAgent(null)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Partner Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Phone</p>
                    <p className="text-gray-900">{selectedAgent.phone || "-"}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Country</p>
                    <p className="text-gray-900">{selectedAgent.country || "-"}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Referral Code</p>
                    <p className="text-emerald-600 font-mono">{selectedAgent.referral_code}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Status</p>
                    <p className={selectedAgent.is_active ? "text-emerald-600" : "text-red-600"}>
                      {selectedAgent.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                {selectedAgent.stats && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Performance Stats</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-emerald-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-600">{selectedAgent.aru_count || 0}</p>
                        <p className="text-xs text-gray-500">ARUs</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{selectedAgent.stats.total_referrals}</p>
                        <p className="text-xs text-gray-500">Total Referrals</p>
                      </div>
                      <div className="bg-amber-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">${selectedAgent.stats.total_earnings?.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">Total Earned</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payout Details */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Payout Information</h3>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Method</p>
                    <p className="text-gray-900 capitalize">{selectedAgent.payout_method?.replace("_", " ") || "-"}</p>
                    {selectedAgent.payout_details && (
                      <p className="text-gray-600 text-sm mt-1">{selectedAgent.payout_details}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => resetPartnerPassword(selectedAgent.id)}
                      disabled={actionLoading === "reset"}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition"
                      data-testid="reset-password-btn"
                    >
                      {actionLoading === "reset" ? "Sending..." : "Send Password Reset"}
                    </button>
                    <button
                      onClick={() => resendInvitation(selectedAgent.id)}
                      disabled={actionLoading === "resend"}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition"
                      data-testid="resend-invitation-btn"
                    >
                      {actionLoading === "resend" ? "Sending..." : "Resend Invitation"}
                    </button>
                    <button
                      onClick={() => toggleAgentStatus(selectedAgent.id, selectedAgent.is_active)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                        selectedAgent.is_active 
                          ? "bg-red-100 text-red-700 hover:bg-red-200" 
                          : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      }`}
                    >
                      {selectedAgent.is_active ? "Deactivate Partner" : "Activate Partner"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminUsersPage = () => {
  const { token, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [token, search]);

  const fetchUsers = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : "";
      const [adminData, agentData] = await Promise.all([
        api.get(`/amt778797/admin-users${query}`, token),
        api.get(`/amt778797/agents?skip=0&limit=50${search ? `&search=${encodeURIComponent(search)}` : ""}`, token)
      ]);
      setAdmins(adminData.admins || []);
      setAgents(agentData.agents || []);
    } catch (error) {
      toast.error("Failed to load user management data");
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (agent) => {
    setActionLoading(`promote-${agent.id}`);
    try {
      const result = await api.post("/amt778797/admin-users/promote", { agent_id: agent.id }, token);
      toast.success(result.message || "Admin access updated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant admin access");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (adminUser) => {
    if (!window.confirm(`Revoke admin access for ${adminUser.email}?`)) return;

    setActionLoading(`revoke-${adminUser.id}`);
    try {
      const result = await api.put(`/amt778797/admin-users/${adminUser.id}/revoke`, {}, token);
      toast.success(result.message || "Admin access revoked");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke admin access");
    } finally {
      setActionLoading(null);
    }
  };

  const adminByEmail = new Map(
    admins.map((adminUser) => [adminUser.email?.toLowerCase(), adminUser])
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">User Management</h1>
          <p className="text-gray-500 mt-1">Promote partners to admin access and revoke admin access without affecting their partner accounts.</p>
        </div>
        <div className="w-full lg:w-80">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        Promoted admins use the same email and password as their partner account when signing into the admin portal.
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800">Current Admin Access</h2>
            <p className="text-sm text-gray-500 mt-1">Active and revoked admin assignments</p>
          </div>

          {admins.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400">No admin users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {admins.map((adminUser) => {
                    const isCurrentUser = adminUser.id === user?.id;
                    return (
                      <tr key={adminUser.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="text-gray-900 font-medium">{adminUser.name}</p>
                          <p className="text-gray-400 text-sm">{adminUser.email}</p>
                          {isCurrentUser && <p className="text-xs text-amber-600 mt-1">Current session</p>}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${adminUser.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                            {adminUser.is_active ? "Active" : "Revoked"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleRevoke(adminUser)}
                            disabled={!adminUser.is_active || isCurrentUser || actionLoading === `revoke-${adminUser.id}`}
                            className="px-3 py-2 text-sm font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === `revoke-${adminUser.id}` ? "Revoking..." : "Revoke Access"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800">Promote Partner Users</h2>
            <p className="text-sm text-gray-500 mt-1">Grant admin access to existing partners</p>
          </div>

          {agents.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400">No partner users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Partner Status</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {agents.map((agent) => {
                    const matchingAdmin = adminByEmail.get(agent.email?.toLowerCase());
                    const hasActiveAdmin = Boolean(matchingAdmin?.is_active);
                    const actionLabel = hasActiveAdmin ? "Already Admin" : matchingAdmin ? "Restore Admin" : "Promote to Admin";

                    return (
                      <tr key={agent.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="text-gray-900 font-medium">{agent.name}</p>
                          <p className="text-gray-400 text-sm">{agent.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className={`text-sm font-medium ${agent.is_active ? "text-emerald-600" : "text-red-600"}`}>
                              {agent.is_active ? "Active partner" : "Inactive partner"}
                            </span>
                            {matchingAdmin && (
                              <span className={`text-xs ${matchingAdmin.is_active ? "text-amber-600" : "text-gray-500"}`}>
                                {matchingAdmin.is_active ? "Also has admin access" : "Had admin access before"}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handlePromote(agent)}
                            disabled={hasActiveAdmin || actionLoading === `promote-${agent.id}`}
                            className="px-3 py-2 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === `promote-${agent.id}` ? "Saving..." : actionLabel}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Referrals Page
const AdminReferralsPage = () => {
  const { token } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchReferrals();
  }, [page]);

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/amt778797/referrals?skip=${page * 20}&limit=20`, token);
      setReferrals(data.referrals);
      setTotal(data.total);
    } catch (error) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    cancelled: "bg-red-100 text-red-700",
    refunded: "bg-orange-100 text-orange-700"
  };

  return (
    <div className="space-y-6" data-testid="admin-referrals">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">All Referrals</h1>
        <span className="text-gray-500">{total} total referrals</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Agent</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Customer</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Product</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Amount</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {referrals.map(ref => (
                  <tr key={ref.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-gray-800 font-medium">{ref.agent?.name || "Unknown"}</p>
                        <p className="text-gray-400 text-sm">{ref.agent?.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-gray-700">{ref.customer_name || "Anonymous"}</p>
                        <p className="text-gray-400 text-sm">{ref.customer_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{ref.product} - {ref.plan}</td>
                    <td className="px-6 py-4 text-amber-600 font-semibold">${ref.amount?.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[ref.status]}`}>
                        {ref.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// Admin Products Page - Configure product signup URLs
const AdminProductsPage = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", signup_url: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await api.get("/amt778797/products", token);
      setProducts(data.products || []);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setEditForm({
      name: product.name || "",
      signup_url: product.signup_url || "",
      description: product.description || ""
    });
  };

  const saveProduct = async () => {
    setSaving(true);
    try {
      await api.put(`/amt778797/products/${editingProduct.id}`, editForm, token);
      toast.success("Product updated!");
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update product");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-500 text-sm">Configure product signup URLs for partner referral links</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Signup URL</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {products.map(product => (
              <tr key={product.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <p className="font-medium text-gray-900">{product.name}</p>
                  <p className="text-xs text-gray-400">{product.description || "No description"}</p>
                </td>
                <td className="px-6 py-4">
                  {product.signup_url ? (
                    <a href={product.signup_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline text-sm break-all">
                      {product.signup_url}
                    </a>
                  ) : (
                    <span className="text-red-500 text-sm">Not configured</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    // Consider product Active only when a signup_url is present
                    // AND either the product is marked active, or the API is integrated.
                    const hasSignup = Boolean(product.signup_url && product.signup_url.trim());
                    const flaggedActive = Boolean(product.is_active);
                    const apiIntegrated = Boolean(product.api_integrated);
                    const isActive = hasSignup && (flaggedActive || apiIntegrated);
                    return (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => openEditModal(product)}
                    className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                    data-testid={`edit-product-${product.id}`}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-semibold text-amber-800 mb-2">How Referral Links Work</h3>
        <p className="text-sm text-amber-700">
          Partners share links like: <code className="bg-amber-100 px-1 rounded">[Signup URL]?ref=[PARTNER_CODE]</code><br/>
          When a customer signs up using that link, your product sends a webhook to track the referral.
        </p>
      </div>

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit {editingProduct.name}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signup URL *</label>
                <input
                  type="url"
                  value={editForm.signup_url}
                  onChange={(e) => setEditForm({ ...editForm, signup_url: e.target.value })}
                  placeholder="https://yourproduct.com/signup"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <p className="text-xs text-gray-400 mt-1">The URL where customers sign up for this product</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingProduct(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={saveProduct}
                disabled={saving}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Payouts Page
const AdminPayoutsPage = () => {
  const { token } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchPayouts();
  }, [page]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/amt778797/payouts?skip=${page * 20}&limit=20`, token);
      setPayouts(data.payouts);
      setTotal(data.total);
    } catch (error) {
      toast.error("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  const updatePayoutStatus = async (payoutId, status) => {
    try {
      await api.put(`/amt778797/payouts/${payoutId}/status?status=${status}`, {}, token);
      toast.success(`Payout marked as ${status}`);
      fetchPayouts();
    } catch (error) {
      toast.error("Failed to update payout");
    }
  };

  const statusColors = {
    pending: "bg-amber-100 text-amber-700",
    processing: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700"
  };

  return (
    <div className="space-y-6" data-testid="admin-payouts">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Payout Requests</h1>
        <span className="text-gray-500">{total} total requests</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
          </div>
        ) : payouts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400">No payout requests yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Agent</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Amount</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Method</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Details</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Date</th>
                  <th className="text-left px-6 py-4 text-gray-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payouts.map(payout => (
                  <tr key={payout.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-gray-800 font-medium">{payout.agent?.name || "Unknown"}</p>
                        <p className="text-gray-400 text-sm">{payout.agent?.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-amber-600 font-semibold">${payout.amount?.toFixed(2)}</td>
                    <td className="px-6 py-4 text-gray-600 capitalize">{payout.method?.replace("_", " ")}</td>
                    <td className="px-6 py-4 text-gray-400 text-sm">{payout.details || "-"}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[payout.status]}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {payout.status === "pending" && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => updatePayoutStatus(payout.id, "completed")}
                            className="px-2 py-1 bg-emerald-50 text-emerald-600 rounded text-xs hover:bg-emerald-100"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => updatePayoutStatus(payout.id, "failed")}
                            className="px-2 py-1 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Admin API Keys Page
const AdminAPIKeysPage = () => {
  const { token } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyDescription, setNewKeyDescription] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const data = await api.get("/amt778797/api-keys", token);
      setApiKeys(data.api_keys);
    } catch (error) {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Please enter a name for the API key");
      return;
    }
    setCreating(true);
    try {
      const data = await api.post("/amt778797/api-keys", {
        name: newKeyName,
        description: newKeyDescription
      }, token);
      setCreatedKey(data.api_key);
      toast.success("API key created!");
      fetchApiKeys();
    } catch (error) {
      toast.error("Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const toggleApiKey = async (keyId) => {
    try {
      await api.put(`/amt778797/api-keys/${keyId}/toggle`, {}, token);
      toast.success("API key status updated");
      fetchApiKeys();
    } catch (error) {
      toast.error("Failed to update API key");
    }
  };

  const deleteApiKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to delete this API key? This action cannot be undone.")) return;
    try {
      await api.delete(`/amt778797/api-keys/${keyId}`, token);
      toast.success("API key deleted");
      fetchApiKeys();
    } catch (error) {
      toast.error("Failed to delete API key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="space-y-8" data-testid="admin-api-keys">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">API Keys</h1>
          <p className="text-gray-500 text-sm mt-1">Manage API keys for your product integrations</p>
        </div>
        <button
          onClick={() => { setShowCreateModal(true); setCreatedKey(null); setNewKeyName(""); setNewKeyDescription(""); }}
          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded transition"
        >
          + Create API Key
        </button>
      </div>

      {/* Info Banner */}
      <div className="flex items-start space-x-3 text-sm text-gray-600 bg-gray-50 p-4 rounded">
        <span className="text-lg">🔐</span>
        <div>
          <p><strong>Publishable Key (pk_live_...):</strong> Safe for frontend. Identifies your product.</p>
          <p><strong>Secret Key (sk_live_...):</strong> Backend only. Required for webhook authentication.</p>
        </div>
      </div>

      {/* API Keys Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg">No API keys yet</p>
          <p className="text-sm">Create your first API key to integrate with your products</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Publishable Key</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Secret Key</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
              <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="text-right py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {apiKeys.map(key => (
              <tr key={key.id} className="hover:bg-gray-50">
                <td className="py-4">
                  <p className="text-gray-900 font-medium">{key.name}</p>
                  <p className="text-gray-400 text-xs">{key.description || "No description"}</p>
                </td>
                <td className="py-4">
                  <div className="flex items-center space-x-2">
                    <code className="text-xs text-gray-600 font-mono">{key.publishable_key}</code>
                    <button onClick={() => copyToClipboard(key.publishable_key)} className="text-gray-400 hover:text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td className="py-4">
                  <code className="text-xs text-gray-400 font-mono">{key.secret_key_preview}</code>
                </td>
                <td className="py-4">
                  <div className="text-sm">
                    <p className="text-gray-900 font-medium">{key.request_count?.toLocaleString() || 0}</p>
                    <p className="text-gray-400 text-xs">{key.success_count || 0} success / {key.error_count || 0} errors</p>
                  </div>
                </td>
                <td className="py-4">
                  <div className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full ${key.is_active ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`}></span>
                    <span className={`text-xs font-medium ${key.is_active ? "text-emerald-600" : "text-gray-400"}`}>
                      {key.is_active ? "Live" : "Inactive"}
                    </span>
                  </div>
                  {key.last_used_at && (
                    <p className="text-gray-400 text-xs mt-1">Last: {new Date(key.last_used_at).toLocaleDateString()}</p>
                  )}
                </td>
                <td className="py-4 text-right">
                  <button
                    onClick={() => toggleApiKey(key.id)}
                    className={`text-xs mr-3 ${key.is_active ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}
                  >
                    {key.is_active ? "Disable" : "Enable"}
                  </button>
                  <button
                    onClick={() => deleteApiKey(key.id)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            {!createdKey ? (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Create API Key</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., Ledger AMT Production"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={newKeyDescription}
                      onChange={(e) => setNewKeyDescription(e.target.value)}
                      placeholder="e.g., Production API key for Ledger AMT"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                  <button
                    onClick={createApiKey}
                    disabled={creating}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded disabled:opacity-50"
                  >
                    {creating ? "Creating..." : "Create Key"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-emerald-600 text-xl">✓</span>
                  <h2 className="text-lg font-semibold text-gray-900">API Key Created</h2>
                </div>
                
                <div className="bg-amber-50 text-amber-800 text-sm p-3 rounded mb-4">
                  ⚠️ Save your Secret Key now. This is the only time you'll see it.
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Publishable Key</label>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-sm bg-gray-50 px-3 py-2 rounded font-mono break-all text-gray-700">{createdKey.publishable_key}</code>
                      <button onClick={() => copyToClipboard(createdKey.publishable_key)} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800">
                        Copy
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Secret Key</label>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-sm bg-amber-50 px-3 py-2 rounded font-mono break-all text-amber-800">{createdKey.secret_key}</code>
                      <button onClick={() => copyToClipboard(createdKey.secret_key)} className="px-3 py-2 text-sm text-amber-700 hover:text-amber-900 font-medium">
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                  <span className="font-medium">Usage:</span> <code className="text-xs">curl -H "X-API-Key: {createdKey.secret_key.substring(0, 20)}..."</code>
                </div>

                <div className="flex justify-end mt-6">
                  <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded">
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Webhook Logs Page
const AdminWebhookLogsPage = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsData, statsData] = await Promise.all([
        api.get(`/amt778797/webhook-logs?skip=${page * 20}&limit=20`, token),
        api.get("/amt778797/webhook-logs/stats", token)
      ]);
      setLogs(logsData.logs);
      setTotal(logsData.total);
      setStats(statsData);
    } catch (error) {
      toast.error("Failed to load webhook logs");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (statusCode) => {
    if (statusCode === 200) return "text-emerald-600";
    if (statusCode === 401) return "text-red-600";
    if (statusCode === 400) return "text-amber-600";
    return "text-gray-600";
  };

  const getEventBadge = (eventType) => {
    const colors = {
      signup: "bg-emerald-100 text-emerald-700",
      trial: "text-blue-600",
      payment: "text-green-600",
      upgrade: "text-purple-600",
      downgrade: "text-orange-600",
      cancel: "text-red-600",
      refund: "text-pink-600"
    };
    return colors[eventType] || "text-gray-600";
  };

  return (
    <div className="space-y-8" data-testid="admin-webhook-logs">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Webhook Logs</h1>
        <p className="text-gray-500 text-sm mt-1">Monitor all incoming webhook events from your products</p>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="flex items-center space-x-8 text-sm">
          <div>
            <span className="text-gray-500">Total</span>
            <span className="ml-2 font-semibold text-gray-900">{stats.total_events?.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500">Successful</span>
            <span className="ml-2 font-semibold text-emerald-600">{stats.successful?.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500">Failed</span>
            <span className="ml-2 font-semibold text-red-600">{stats.failed?.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500">Success Rate</span>
            <span className="ml-2 font-semibold text-gray-900">{stats.success_rate}%</span>
          </div>
        </div>
      )}

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg">No webhook events yet</p>
          <p className="text-sm">Events will appear here when products send data to the webhook</p>
        </div>
      ) : (
        <>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map(log => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="py-4 text-gray-500 text-sm">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="py-4">
                    <span className={`text-sm font-medium ${getEventBadge(log.event_type)}`}>
                      {log.event_type}
                    </span>
                  </td>
                  <td className="py-4 text-gray-600 text-sm">
                    {log.request_body?.product || "-"}
                  </td>
                  <td className="py-4 text-gray-600 text-sm">
                    {log.api_key_name || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="py-4">
                    <span className={`text-sm font-medium ${getStatusColor(log.status_code)}`}>
                      {log.status_code}
                    </span>
                    {log.error_message && (
                      <p className="text-red-500 text-xs mt-1">{log.error_message}</p>
                    )}
                  </td>
                  <td className="py-4 text-gray-400 text-sm text-right">
                    {log.processing_time_ms?.toFixed(0)}ms
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {total > 20 && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                ← Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {page + 1} of {Math.ceil(total / 20)}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * 20 >= total}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Layouts
const AgentLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-gray-50">
      <AgentNavbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

const AdminLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-gray-50">
      <AdminNavbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

// Verify Email Page
const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [revealedCode, setRevealedCode] = useState(null);

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (!email || !code) {
      toast.error("Email and code are required");
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/auth/verify-email?email=${encodeURIComponent(email)}&code=${code}`);
      toast.success("Email verified! Redirecting to login...");
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("Enter your email to resend verification");
      return;
    }
    setResendLoading(true);
    try {
      const resp = await api.post('/auth/resend-verification', { email, reveal_code: true });
      toast.success(resp.message || 'Verification resent');
      if (resp.verification_code) setRevealedCode(resp.verification_code);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend verification");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 border border-emerald-100 shadow-xl">
          <h2 className="text-xl font-semibold mb-4">Verify your email</h2>
          <div className="space-y-3">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full px-3 py-2 border rounded" />
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} className="flex-1 px-3 py-2 border rounded" />
              <button onClick={handleVerify} disabled={loading} className="px-4 py-2 bg-emerald-600 text-white rounded">{loading ? 'Verifying...' : 'Verify'}</button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleResend} disabled={resendLoading} className="px-3 py-2 text-sm text-emerald-700 bg-white border border-emerald-100 rounded hover:bg-emerald-50">{resendLoading ? 'Sending...' : 'Resend verification'}</button>
              {revealedCode && <div className="text-xs text-gray-600">Code: <strong className="ml-1">{revealedCode}</strong></div>}
            </div>
            <div className="text-sm text-gray-500 mt-3">If the verification link expired, use resend to get a new code or click the link in the email.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// App
function App() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
        await axios.post(`${API}/amt778797/seed`);
      } catch (e) {
        // Already seeded or error - ignore
      }
    };
    seedData();
  }, []);

  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          {/* Agent Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/apply" element={<ApplicationPage />} />
          <Route path="/login" element={<AgentLoginPage />} />
          <Route path="/register" element={<AgentRegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/setup-account" element={<SetupAccountPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute requiredType="agent">
              <AgentLayout><AgentDashboardPage /></AgentLayout>
            </ProtectedRoute>
          } />
          <Route path="/referrals" element={
            <ProtectedRoute requiredType="agent">
              <AgentLayout><AgentReferralsPage /></AgentLayout>
            </ProtectedRoute>
          } />
          <Route path="/commissions" element={
            <ProtectedRoute requiredType="agent">
              <AgentLayout><AgentCommissionsPage /></AgentLayout>
            </ProtectedRoute>
          } />
          <Route path="/wallet" element={
            <ProtectedRoute requiredType="agent">
              <AgentLayout><AgentWalletPage /></AgentLayout>
            </ProtectedRoute>
          } />
          <Route path="/links" element={
            <ProtectedRoute requiredType="agent">
              <AgentLayout><AgentLinksPage /></AgentLayout>
            </ProtectedRoute>
          } />
          <Route path="/recommendations" element={
            <ProtectedRoute requiredType="agent">
              <AgentLayout><AgentRecommendationsPage /></AgentLayout>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute requiredType="agent">
              <AgentLayout><AgentProfilePage /></AgentLayout>
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/amt778797/login" element={<AdminLoginPage />} />
          <Route path="/amt778797/forgot-password" element={<ForgotPasswordPage loginPath="/amt778797/login" />} />
          <Route path="/amt778797/dashboard" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminDashboardPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/applications" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminApplicationsPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/agents" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminAgentsPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/users" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminUsersPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/products" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminProductsPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/referrals" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminReferralsPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/api-keys" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminAPIKeysPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/webhook-logs" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminWebhookLogsPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/amt778797/payouts" element={
            <ProtectedRoute requiredType="admin">
              <AdminLayout><AdminPayoutsPage /></AdminLayout>
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
